import React, { useState, useEffect } from 'react';
import { Cidade, Estado } from '../../types/sistema';
import { sistemaService } from '../../services/sistemaService';
import { X, Building, Save, Loader2, AlertCircle } from 'lucide-react';

interface CidadeFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  cidade: Cidade | null;
  estados: Estado[];
  defaultEstadoId?: number;
  onSuccess: () => void;
}

export const CidadeFormModal: React.FC<CidadeFormModalProps> = ({
  isOpen,
  onClose,
  cidade,
  estados,
  defaultEstadoId,
  onSuccess,
}) => {
  const [nome, setNome] = useState('');
  const [estadoId, setEstadoId] = useState<number | ''>('');
  const [codigoIbge, setCodigoIbge] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setNome(cidade?.nome || '');
      setEstadoId(cidade?.estado_id || defaultEstadoId || (estados[0]?.id ?? ''));
      setCodigoIbge(cidade?.codigo_ibge || '');
      setError(null);
    }
  }, [isOpen, cidade, estados, defaultEstadoId]);

  if (!isOpen) return null;

  const isEditing = Boolean(cidade && cidade.id);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!nome.trim()) {
      setError('Por favor, informe o nome da cidade.');
      return;
    }
    if (!estadoId) {
      setError('Por favor, selecione o estado (UF) da cidade.');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload = {
        nome: nome.trim(),
        estado_id: Number(estadoId),
        codigo_ibge: codigoIbge.trim() || '',
      };

      if (isEditing && cidade) {
        const res = await sistemaService.updateCidade(cidade.id, payload);
        if (!res.success) {
          setError(res.error || 'Erro ao atualizar cidade.');
          setLoading(false);
          return;
        }
      } else {
        const res = await sistemaService.createCidade(payload);
        if (!res.success) {
          setError(res.error || 'Erro ao cadastrar cidade.');
          setLoading(false);
          return;
        }
      }

      onSuccess();
      onClose();
    } catch (err: any) {
      console.error('Erro ao salvar cidade:', err);
      setError(err.message || 'Erro inesperado ao salvar cidade.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-xs">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden border border-slate-100 animate-in fade-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50/50">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-red-50 text-red-600 rounded-xl">
              <Building className="w-5 h-5" />
            </div>
            <div>
              <h3 className="font-bold text-slate-800 text-base">
                {isEditing ? 'Editar Cidade' : 'Nova Cidade'}
              </h3>
              <p className="text-xs text-slate-500">
                {isEditing ? `Alterando dados da cidade #${cidade?.id}` : 'Cadastre um novo município para rotas e endereços'}
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-slate-400 hover:text-slate-600 p-1.5 rounded-lg hover:bg-slate-100 transition-colors cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div className="p-6 space-y-4">
            {error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-xs flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-500 shrink-0 mt-0.5" />
                <span>{error}</span>
              </div>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Nome do Município / Cidade <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
                placeholder="Ex: São Paulo, Campinas, Santos, Niterói..."
                required
                autoFocus
                className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:bg-white text-slate-800 placeholder-slate-400 font-medium transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Estado (UF) Pertencente <span className="text-red-500">*</span>
              </label>
              <select
                value={estadoId}
                onChange={(e) => setEstadoId(Number(e.target.value))}
                required
                className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:bg-white text-slate-800 transition-all font-medium"
              >
                <option value="">Selecione o Estado...</option>
                {estados.map((est) => (
                  <option key={est.id} value={est.id}>
                    {est.nome} ({est.sigla || (est as any).uf})
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1.5">
                Código IBGE <span className="text-slate-400 font-normal">(opcional)</span>
              </label>
              <input
                type="text"
                value={codigoIbge}
                onChange={(e) => setCodigoIbge(e.target.value)}
                placeholder="Ex: 3550308"
                className="w-full px-3.5 py-2 bg-slate-50 border border-slate-200 rounded-xl text-sm focus:outline-hidden focus:ring-2 focus:ring-red-500 focus:bg-white text-slate-800 placeholder-slate-400 font-mono transition-all"
              />
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/50 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center gap-2 px-5 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold shadow-xs hover:shadow-md transition-all disabled:opacity-50 cursor-pointer"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Salvando...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  {isEditing ? 'Salvar Alterações' : 'Cadastrar Cidade'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
